import type { ReactNode, RefObject } from 'react'
import {
  faCheck,
  faCommentQuestion,
  faShieldUser,
} from '@fortawesome/pro-solid-svg-icons'

import { Banner, BannerVariants, Button, Icon, Image, Text } from '@/ui'
import { TypographyStyles, TextColors, mtc } from '@/styles'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectSeparationBusinessCount,
  selectSeparationCounts,
  selectSeparationPersonalCount,
  selectSeparationUnasked,
} from '@/features/onboarding/separation'
import { AI_ASSISTANT_ICON_URL } from './booksQuestion.constants'
import { ONBOARDING_SCREEN } from '../shared/onboardingScreen.styles'
import { plural, transactionNoun } from '../shared/plural'

interface ClarifiedCounts {
  business: number
  personal: number
  unsure: number
}

interface SummaryStepProps {
  /** Tailwind background class for the step's icon chip. */
  iconClassName: string
  icon: typeof faCheck
  title: string
  children: ReactNode
}

const SummaryStep = ({
  iconClassName,
  icon,
  title,
  children,
}: SummaryStepProps) => (
  <div className="flex w-full flex-col py-2">
    <div className="flex w-full items-center gap-4">
      <div
        className={mtc(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
          iconClassName
        )}
      >
        <Icon icon={icon} color="text-neutral-0" size="1x" />
      </div>
      <Text variant={TypographyStyles.LABEL_MD} color={TextColors.HEADER}>
        {title}
      </Text>
    </div>
    <div className="pr-6 pl-12">
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.EMPHASIS}>
        {children}
      </Text>
    </div>
  </div>
)

/** How a bucket's total splits between what the user answered and what pre-sort claimed. */
const SortedBy = ({
  confirmed,
  total,
}: {
  confirmed: number
  total: number
}) => {
  const detected = total - confirmed
  return (
    <>
      {confirmed > 0 &&
        (total === 1 ? (
          <>You confirmed this one. </>
        ) : (
          <>
            You confirmed <strong>{confirmed}</strong> of these.{' '}
          </>
        ))}
      {detected > 0 && (
        <>
          <strong>{detected}</strong> {plural(detected, 'was', 'were')} detected
          automatically.{' '}
        </>
      )}
    </>
  )
}

interface SortingSummaryProps {
  clarifiedCounts: ClarifiedCounts
  releaseFailed: boolean
  isRetryingRelease: boolean
  completionFailed: boolean
  isCompleting: boolean
  checkIconRef: RefObject<HTMLDivElement>
  onRetryRelease: () => void
  onGoToHome: () => void
  onBack: () => void
}

export const SortingSummary = ({
  clarifiedCounts,
  releaseFailed,
  isRetryingRelease,
  completionFailed,
  isCompleting,
  checkIconRef,
  onRetryRelease,
  onGoToHome,
  onBack,
}: SortingSummaryProps) => {
  const businessCount = useReselector(selectSeparationBusinessCount)
  const personalCount = useReselector(selectSeparationPersonalCount)
  const unasked = useReselector(selectSeparationUnasked)
  const unaskedCount = useReselector(selectSeparationCounts).unresolved
  const whatHappensNext = [
    clarifiedCounts.business > 0 &&
      "We're auto-categorizing the transactions you marked as business.",
    clarifiedCounts.unsure > 0 && "We've sent your notes to your bookkeeper.",
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={ONBOARDING_SCREEN.page}>
      <div
        className={mtc(
          ONBOARDING_SCREEN.content,
          'flex-1 justify-center text-center'
        )}
      >
        <div className="flex h-20 w-20" ref={checkIconRef}>
          <Image
            src={AI_ASSISTANT_ICON_URL}
            alt=""
            objectFit="contain"
            className="h-full w-full"
          />
        </div>
        <div
          className={mtc(
            ONBOARDING_SCREEN.header,
            ONBOARDING_SCREEN.narrowColumn
          )}
        >
          <Text variant={TypographyStyles.DISPLAY_SM} textAlign="center">
            Thanks for your answers.
          </Text>
          <Text
            variant={TypographyStyles.BODY_SM}
            color={TextColors.MUTED}
            textAlign="center"
            className="text-center"
          >
            {whatHappensNext && (
              <>
                {whatHappensNext}
                <br />
                <br />
              </>
            )}
            Next, your dashboard will show where you are in the catchup
            bookkeeping process. We&apos;ll flag anything else we need from you
            right there.
          </Text>
        </div>
        {releaseFailed && (
          <div
            role="alert"
            className="flex max-w-[450px] flex-col items-center gap-3"
          >
            <Text
              variant={TypographyStyles.BODY_SM}
              color={TextColors.DANGER}
              textAlign="center"
            >
              Your answers are saved, but we couldn&apos;t hand them to
              categorization. Retry so your books finish updating.
            </Text>
            <Button
              variant="secondary"
              onClick={onRetryRelease}
              disabled={isRetryingRelease}
            >
              {isRetryingRelease ? 'Retrying…' : 'Try again'}
            </Button>
          </div>
        )}
        {completionFailed && (
          <div role="alert" className="max-w-[450px]">
            <Text
              variant={TypographyStyles.BODY_SM}
              color={TextColors.DANGER}
              textAlign="center"
            >
              We couldn&apos;t finish setting up your account. Your sorting is
              saved, so try again.
            </Text>
          </div>
        )}
        {/* Stretched, not centred: the pair reads as one control stack only if the
            narrower label takes the wider one's width. */}
        <div className="flex flex-col gap-2">
          <Button
            variant="primary"
            onClick={onGoToHome}
            disabled={isCompleting}
          >
            {isCompleting ? 'Finishing up…' : 'Go to Dashboard'}
          </Button>
          <Button variant="secondary" onClick={onBack}>
            Back
          </Button>
        </div>

        <div
          className={mtc(
            ONBOARDING_SCREEN.surfaceCard,
            ONBOARDING_SCREEN.narrowColumn,
            'gap-6 text-left'
          )}
        >
          <Text variant={TypographyStyles.H1}>Transaction sorting summary</Text>
          <div className="flex w-full flex-col">
            {businessCount > 0 && (
              <SummaryStep
                iconClassName="bg-green-50"
                icon={faCheck}
                title={`${businessCount.toLocaleString()} business ${transactionNoun(businessCount)}`}
              >
                <SortedBy
                  confirmed={clarifiedCounts.business}
                  total={businessCount}
                />
                {plural(
                  businessCount,
                  "We'll categorize it for you.",
                  "We'll categorize them for you."
                )}
              </SummaryStep>
            )}
            {personalCount > 0 && (
              <SummaryStep
                iconClassName="bg-green-50"
                icon={faCheck}
                title={`${personalCount.toLocaleString()} personal ${transactionNoun(personalCount)}`}
              >
                <SortedBy
                  confirmed={clarifiedCounts.personal}
                  total={personalCount}
                />
                {plural(
                  personalCount,
                  "We'll keep it out of your books.",
                  "We'll keep them out of your books."
                )}
              </SummaryStep>
            )}
            {clarifiedCounts.unsure > 0 && (
              <SummaryStep
                iconClassName="bg-yellow-100"
                icon={faCommentQuestion}
                title={`${clarifiedCounts.unsure} marked unsure`}
              >
                Our bookkeeping team will follow up directly on the notes you
                added.
              </SummaryStep>
            )}
          </div>
          {unasked.length > 0 && (
            <div className="mt-4 flex flex-col gap-2 rounded-xl border border-neutral-100 p-4">
              <Text
                variant={TypographyStyles.LABEL_MD}
                color={TextColors.HEADER}
              >
                We&apos;ll categorize {plural(unaskedCount, 'this', 'these')}{' '}
                for you
              </Text>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                {unaskedCount.toLocaleString()} {transactionNoun(unaskedCount)}{' '}
                we didn&apos;t ask about. Nothing to do here:{' '}
                {plural(
                  unaskedCount,
                  "it's listed so you know where it went.",
                  "they're listed so you know where they went."
                )}
              </Text>
              {unasked.map((group) => (
                <div
                  key={group.displayName}
                  className="flex items-baseline justify-between gap-4 border-t border-neutral-50 py-1.5"
                >
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    className="min-w-0 truncate"
                  >
                    {group.displayName}
                  </Text>
                  {/* Two right-aligned columns rather than one run-together string, so the
                      amounts line up down the list instead of ending wherever the count
                      leaves them. */}
                  <div className="flex shrink-0 items-baseline gap-6">
                    <Text
                      variant={TypographyStyles.BODY_SM}
                      color={TextColors.MUTED}
                      className="min-w-6 text-right tabular-nums"
                    >
                      {group.transactionCount.toLocaleString()}
                    </Text>
                    <Text
                      variant={TypographyStyles.BODY_SM}
                      color={TextColors.MUTED}
                      className="min-w-20 text-right tabular-nums"
                    >
                      {group.amount}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Banner
            variant={BannerVariants.DEFAULT}
            icon={faShieldUser}
            wrapperClassName="w-full justify-start"
          >
            Our bookkeepers will review everything to double-check accuracy.
          </Banner>
        </div>
      </div>
    </div>
  )
}
